'use client';

import { motion } from 'framer-motion';
import { Button } from '@/components/shared/Button';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import type { AmbassadorContent } from '@/lib/content-schema';

interface Props {
  content: AmbassadorContent['hero'];
}

export function HeroAmbassador({ content }: Props) {
  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-zibarit-marron-oscuro via-zibarit-verde-oscuro to-zibarit-gris-oscuro overflow-hidden">
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
      >
        <div className="text-center lg:text-left">
          <motion.h1
            variants={fadeInUp}
            className="text-title text-4xl md:text-5xl lg:text-6xl text-zibarit-bone mb-6"
          >
            <span className="text-zibarit-verde-brillante">{content.titleAccent}</span>
            {content.titleRest}
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            className="text-body text-base md:text-lg text-zibarit-bone/80 max-w-xl mb-10"
          >
            {content.subtitle}
            <span className="text-zibarit-verde-brillante font-semibold italic">
              {content.subtitleAccent}
            </span>
          </motion.p>

          <motion.div
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
          >
            {content.ctaPrimaryVisible && (
              <Button href="#aplicar" magnetic size="lg">
                {content.ctaPrimaryText}
              </Button>
            )}
            {content.ctaSecondaryVisible && (
              <Button
                href="#como-funciona"
                variant="secondary"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-zibarit-negro"
              >
                {content.ctaSecondaryText}
              </Button>
            )}
          </motion.div>
        </div>

        <div className="relative hidden lg:flex items-center justify-center h-[400px]">
          <div className="absolute w-64 h-64 rounded-full bg-zibarit-verde-brillante/20 blur-3xl animate-float" />
          <div
            className="absolute w-48 h-48 rounded-full bg-zibarit-marron-oscuro/30 blur-2xl animate-float"
            style={{ animationDelay: '-7s', top: '20%', left: '60%' }}
          />
          <div
            className="absolute w-32 h-32 rounded-full bg-zibarit-verde-oscuro/40 blur-2xl animate-float"
            style={{ animationDelay: '-13s', bottom: '10%', left: '30%' }}
          />
        </div>
      </motion.div>
    </section>
  );
}
